import { useEffect, useMemo, useState } from 'react';
import { ChevronDown, ChevronRight, Folder } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  allSourceFolderGuids,
  collectSubtreeGuids,
  partitionSourceFolders,
  type SourceFolderNode,
} from '@/lib/deadlines/source-folders';
import { FilterCountBadge, useToolbarCompact } from '@/components/ui/data-table-filter-toolbar';
import { toolbarFilterLabelClass } from '@/lib/toolbar-compact';
import { cn } from '@/lib/utils';

function FolderRow({
  folder,
  depth,
  selectedIds,
  expandedIds,
  onToggle,
  onToggleExpanded,
}: {
  folder: SourceFolderNode;
  depth: number;
  selectedIds: Set<string>;
  expandedIds: Set<string>;
  onToggle: (folder: SourceFolderNode) => void;
  onToggleExpanded: (id: string) => void;
}) {
  const descendantIds = collectSubtreeGuids(folder);
  const selectedCount = descendantIds.filter((id) => selectedIds.has(id)).length;
  const allSelected = selectedCount === descendantIds.length;
  const someSelected = selectedCount > 0 && !allSelected;
  const hasChildren = folder.children.length > 0;
  const isExpanded = expandedIds.has(folder.guid);

  return (
    <div>
      <div
        className="flex w-full items-center gap-1.5 rounded-md px-1.5 py-1 text-sm text-gray-800 hover:bg-gray-50"
        style={{ paddingLeft: `${depth * 14 + 6}px` }}
      >
        {hasChildren ? (
          <button
            type="button"
            className="flex h-5 w-5 shrink-0 items-center justify-center rounded-xs hover:bg-gray-200"
            onClick={() => onToggleExpanded(folder.guid)}
            aria-label={isExpanded ? `Collapse ${folder.name}` : `Expand ${folder.name}`}
          >
            {isExpanded ? (
              <ChevronDown className="h-3.5 w-3.5 text-gray-400" />
            ) : (
              <ChevronRight className="h-3.5 w-3.5 text-gray-400" />
            )}
          </button>
        ) : (
          <span className="h-5 w-5 shrink-0" />
        )}
        <button
          type="button"
          onClick={() => onToggle(folder)}
          className="flex min-w-0 flex-1 items-center gap-1.5 text-left"
          role="checkbox"
          aria-checked={allSelected ? true : someSelected ? 'mixed' : false}
          aria-label={folder.name}
        >
          <span
            aria-hidden
            className={cn(
              'flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded-xs border border-stone-900',
              allSelected && 'bg-primary text-primary-foreground',
              someSelected && !allSelected && 'bg-primary/40',
            )}
          >
            {allSelected ? <span className="block h-1.5 w-1.5 bg-current" /> : null}
          </span>
          <Folder className="h-3.5 w-3.5 shrink-0 fill-amber-400/15 text-amber-500/40" />
          <span className="min-w-0 truncate">{folder.name}</span>
        </button>
      </div>
      {hasChildren && isExpanded && (
        <div>
          {folder.children.map((child) => (
            <FolderRow
              key={child.guid}
              folder={child}
              depth={depth + 1}
              selectedIds={selectedIds}
              expandedIds={expandedIds}
              onToggle={onToggle}
              onToggleExpanded={onToggleExpanded}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function FolderSection({
  title,
  folders,
  selectedIds,
  expandedIds,
  onToggle,
  onToggleExpanded,
}: {
  title?: string;
  folders: SourceFolderNode[];
  selectedIds: Set<string>;
  expandedIds: Set<string>;
  onToggle: (folder: SourceFolderNode) => void;
  onToggleExpanded: (id: string) => void;
}) {
  return (
    <div className={title ? 'pt-1' : undefined}>
      {title && (
        <div className="px-2 pb-1 pt-1.5 text-[11px] font-semibold uppercase tracking-[0.06em] text-gray-400">
          {title}
        </div>
      )}
      {folders.map((folder) => (
        <FolderRow
          key={folder.guid}
          folder={folder}
          depth={0}
          selectedIds={selectedIds}
          expandedIds={expandedIds}
          onToggle={onToggle}
          onToggleExpanded={onToggleExpanded}
        />
      ))}
    </div>
  );
}

export function DeadlinesDefaultFilters({
  folders,
  selectedGuids,
  onSelectedGuidsChange,
  region,
  forum,
  isRestricted,
  isDefaultSelection,
}: {
  folders: SourceFolderNode[];
  selectedGuids: Set<string>;
  onSelectedGuidsChange: (next: Set<string> | null) => void;
  region: string;
  forum: string | null;
  /** True when the page is actually filtering by source folders (not inferred from selection size). */
  isRestricted: boolean;
  /** True when the page is using canonical default folders, not a user override. */
  isDefaultSelection: boolean;
}) {
  const [open, setOpen] = useState(false);
  const allGuids = useMemo(() => allSourceFolderGuids(folders), [folders]);
  const { defaults, rest } = useMemo(
    () => partitionSourceFolders(folders, region, forum),
    [folders, forum, region],
  );
  const allSelected = allGuids.length > 0 && allGuids.every((id) => selectedGuids.has(id));

  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [seenFolderIds, setSeenFolderIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    const toExpand: string[] = [];
    const newlySeen: string[] = [];
    const visit = (nodes: SourceFolderNode[]) => {
      for (const node of nodes) {
        if (!seenFolderIds.has(node.guid)) {
          newlySeen.push(node.guid);
          if (node.children.length > 0) toExpand.push(node.guid);
        }
        visit(node.children);
      }
    };
    visit(folders);
    if (newlySeen.length === 0) return;
    setSeenFolderIds((prev) => {
      const next = new Set(prev);
      for (const id of newlySeen) next.add(id);
      return next;
    });
    if (toExpand.length === 0) return;
    setExpandedIds((prev) => {
      const next = new Set(prev);
      for (const id of toExpand) next.add(id);
      return next;
    });
  }, [folders, seenFolderIds]);

  const toggleFolder = (folder: SourceFolderNode) => {
    const ids = collectSubtreeGuids(folder);
    const next = new Set(selectedGuids);
    const allOn = ids.every((id) => next.has(id));
    if (allOn) ids.forEach((id) => next.delete(id));
    else ids.forEach((id) => next.add(id));
    onSelectedGuidsChange(next);
  };

  const toggleExpanded = (id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // Restore a hover tooltip only when the toolbar collapses the label to an
  // icon, so the button still names itself without a redundant tooltip when the
  // label text is visible.
  const isCompact = useToolbarCompact();

  const trigger = (
    <PopoverTrigger asChild>
      <Button
        variant="outline"
        size="sm"
        className={cn(
          'relative h-8 shrink-0 gap-1.5 overflow-visible text-gray-700',
          isRestricted && 'bg-accent text-accent-foreground',
        )}
        aria-label="Source folders"
      >
        <Folder className="h-4 w-4 text-amber-500/40 fill-amber-400/15" />
        <span className={toolbarFilterLabelClass}>Source Folders</span>
        <FilterCountBadge count={isRestricted ? selectedGuids.size : 0} />
      </Button>
    </PopoverTrigger>
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      {isCompact ? (
        <TooltipProvider delayDuration={100}>
          <Tooltip>
            <TooltipTrigger asChild>{trigger}</TooltipTrigger>
            <TooltipContent side="top" className="text-xs font-normal">
              Source folders
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      ) : (
        trigger
      )}
      <PopoverContent align="start" className="w-95 p-0">
        <div className="border-b border-gray-100 px-3.5 pb-2.5 pt-3">
          <div className="text-sm font-medium text-gray-900">Source folders</div>
          <p className="mt-0.5 text-xs leading-relaxed text-gray-500">
            Only include deadlines extracted from these folders.
          </p>
        </div>

        <div className="flex items-center justify-between gap-2 px-3.5 pb-1.5 pt-3">
          <div className="flex items-baseline gap-2">
            <span className="text-[11px] font-semibold uppercase tracking-[0.06em] text-gray-500">
              Folders
            </span>
            <span className="text-[11px] tabular-nums text-gray-400">
              {isRestricted ? `${selectedGuids.size} of ${allGuids.length}` : 'All'}
            </span>
          </div>
          <div className="flex items-center gap-2">
            {!allSelected && (
              <button
                type="button"
                onClick={() => onSelectedGuidsChange(new Set(allGuids))}
                className="text-[11px] font-medium text-gray-500 hover:text-gray-800"
              >
                All
              </button>
            )}
            {!isDefaultSelection && (
              <button
                type="button"
                onClick={() => onSelectedGuidsChange(null)}
                className="text-[11px] font-medium text-gray-500 hover:text-gray-800"
              >
                Reset defaults
              </button>
            )}
          </div>
        </div>

        <div className="custom-scrollbar max-h-72 overflow-y-auto px-1.5 pb-2">
          {folders.length === 0 ? (
            <p className="px-2 py-3 text-sm text-gray-400">No folders on this matter.</p>
          ) : (
            <>
              {defaults.length > 0 && (
                <FolderSection
                  title="Default"
                  folders={defaults}
                  selectedIds={selectedGuids}
                  expandedIds={expandedIds}
                  onToggle={toggleFolder}
                  onToggleExpanded={toggleExpanded}
                />
              )}
              {rest.length > 0 && (
                <FolderSection
                  title={defaults.length > 0 ? 'Other folders' : undefined}
                  folders={rest}
                  selectedIds={selectedGuids}
                  expandedIds={expandedIds}
                  onToggle={toggleFolder}
                  onToggleExpanded={toggleExpanded}
                />
              )}
            </>
          )}
        </div>

        <div className="flex items-center justify-end border-t border-gray-100 px-3.5 py-2.5">
          <Button size="sm" variant="outline" onClick={() => setOpen(false)}>
            Done
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
